import { LiveUpdate } from '@capawesome/capacitor-live-update'

import { getWebAppBaseUrl, isNativePlatform } from '@/lib/native/platform'

export interface NativeLiveUpdateInfo {
  bundleId: string
  minNativeVersion: string
  url: string
  checksum: string
  builtAt: string
}

const RESERVED_BUNDLE_ID = 'public'

export function isVersionLessThan(current: string, minimum: string): boolean {
  const currentParts = current.split('.').map(part => Number.parseInt(part, 10) || 0)
  const minimumParts = minimum.split('.').map(part => Number.parseInt(part, 10) || 0)
  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const a = currentParts[i] ?? 0
    const b = minimumParts[i] ?? 0
    if (a < b) return true
    if (a > b) return false
  }
  return false
}

function isNativeLiveUpdateInfo(value: unknown): value is NativeLiveUpdateInfo {
  if (!value || typeof value !== 'object') return false
  const info = value as Record<string, unknown>
  return (
    typeof info.bundleId === 'string' &&
    info.bundleId.length > 0 &&
    info.bundleId !== RESERVED_BUNDLE_ID &&
    typeof info.minNativeVersion === 'string' &&
    info.minNativeVersion.length > 0 &&
    typeof info.url === 'string' &&
    info.url.startsWith('https://') &&
    typeof info.checksum === 'string' &&
    /^[a-f0-9]{64}$/i.test(info.checksum) &&
    typeof info.builtAt === 'string'
  )
}

export async function markLiveUpdateReady(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    await LiveUpdate.ready()
  } catch {
    // The plugin is only present in a native shell that includes it.
  }
}

/**
 * Downloads a newer web bundle and stages it for the next launch.
 * Returns true when a bundle is staged and a reload will apply it.
 */
export async function syncNativeLiveUpdate(): Promise<boolean> {
  if (!isNativePlatform() || import.meta.env.DEV) return false

  try {
    const response = await fetch(`${getWebAppBaseUrl()}/native-updates/latest.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return false

    const data: unknown = await response.json()
    if (!isNativeLiveUpdateInfo(data)) return false

    const { versionName } = await LiveUpdate.getVersionName()
    if (isVersionLessThan(versionName || '0.0.0', data.minNativeVersion)) return false

    const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle()
    if (currentBundleId === data.bundleId) return false

    const { bundleId: nextBundleId } = await LiveUpdate.getNextBundle()
    if (nextBundleId === data.bundleId) return true

    try {
      await LiveUpdate.downloadBundle({
        bundleId: data.bundleId,
        url: data.url,
        checksum: data.checksum,
      })
    } catch (error) {
      if (!isBundleAlreadyDownloaded(error)) return false
    }
    await LiveUpdate.setNextBundle({ bundleId: data.bundleId })
    return true
  } catch {
    return false
  }
}

export async function reloadNativeLiveUpdate(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    await LiveUpdate.reload()
  } catch {
    // The staged bundle still applies the next time the process starts.
  }
}

function isBundleAlreadyDownloaded(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('message' in error)) return false
  return String(error.message).toLowerCase().includes('already exists')
}
